import { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessibleOrganizations, setAccessibleOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Charger l'utilisateur depuis le token stocké
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await api.get('/auth/me');
        setUser(response.data.user);
        setAccessibleOrganizations(response.data.accessibleOrganizations || []);
      } catch (err) {
        console.error('Erreur init auth:', err);
        localStorage.removeItem('token');
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    setError(null);
    
    try {
      const response = await api.post('/auth/login', { email, password });
      const { token, user: userData } = response.data;
      
      localStorage.setItem('token', token);
      setUser(userData);
      
      // Récupérer le profil complet (avec avatarUrl) et les organisations accessibles
      const meResponse = await api.get('/auth/me');
      setUser(meResponse.data.user);
      setAccessibleOrganizations(meResponse.data.accessibleOrganizations || []);
      
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error || 'Erreur de connexion';
      setError(message);
      return { success: false, error: message, needsPasswordSetup: err.response?.data?.needsPasswordSetup };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setAccessibleOrganizations([]);
  };

  // Mettre à jour partiellement l'objet user (ex : avatarUrl après upload)
  const updateUser = (updates) => {
    setUser((prev) => prev ? { ...prev, ...updates } : prev);
  };

  const isAgent = user && ['agent', 'admin', 'superadmin'].includes(user.role);
  const isAdmin = user && ['admin', 'superadmin'].includes(user.role);
  const isSuperAdmin = user && user.role === 'superadmin';

  const value = {
    user,
    accessibleOrganizations,
    loading,
    error,
    login,
    logout,
    updateUser,
    isAuthenticated: !!user,
    isAgent,
    isAdmin,
    isSuperAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
